import { Dispatch, UnknownAction } from 'redux';
import { AppState } from '../types/state/AppState';
import { amrPipelineService } from '../services/amr-pipeline.service';
import { amrPipelineActions as actionTypes, constants } from '../constants';
import { errorActions } from './error.actions';
import { DealsPipelineFilters } from '../types/state/PipelineFilterState';
import { ThunkDispatch } from 'redux-thunk';
import { user } from '../user';
import { Company } from '../types/amr-pipeline/models/Company';
import moment from 'moment';
import 'moment-timezone';
import { saveAs } from 'file-saver';
import { FilterSelectGroup } from '../types/filters/FilterGroup';
import { createSearchTransactionActions } from './search-transactions.actions';
import { convertPipelineViewToExportedFromKey, PipelineView } from '../components/amrPipeline/types/PipelineView';
import { PipelineType } from '../types/amr-pipeline/enums/PipelineType';
import {
    amrPipelineSelector,
    amrPipelineFilterSelector,
    issuanceMonitorFilterSelector,
    summarySelector,
} from '../selectors/amr-pipeline.selector';
import {
    groupSearchByDealAndClassIndicators,
    getFilterCriteria,
    mergeSortCriteria,
    getKMatrixSortCriteria,
    getStatusFilters,
} from '../utils/amr-pipeline.utils';
import { SubscriptionFeature } from '../types/billing/SubscriptionFeature';
import { UserConfigType } from '../types/user-config/UserConfigType';
import { isNil, round } from 'lodash';
import { dateRangeFilterOptions } from '../constants/date-range.filter';
import { queryStringSerializer } from '../utils/filtering';
import { QueryStringArgumentSerializer } from '../utils/filtering/serializers/QueryStringArgumentSerializer';
import {
    savedFilter,
    collateralManagers,
    arrangers,
    dealsLegalNames,
    deals as dealsSerializer,
    transactionTypes,
    trustees,
    multipleCurrencies,
    reinvestmentEndFrom,
    reinvestmentEndTo,
    nonCallEndFrom,
    nonCallEndTo,
    pricingDateFrom,
    pricingDateTo,
    ratings,
    coupon,
    esg,
    euCompliance,
    staticDeals,
    transactionStatuses,
    amr,
    collateralTypes,
    outOfNC,
    outOfRI,
    closingDateFrom,
    closingDateTo,
    rollerDeadlineFrom,
    rollerDeadlineTo,
    wasFrom,
    wasTo,
    warfFrom,
    warfTo,
    dsFrom,
    dsTo,
    sofr,
    debutClo,
    mvocFrom,
    mvocTo,
    vintageYearFrom,
    vintageYearTo,
    ratingAgencies,
    hasNotes,
    classNames,
    mixedDateRange,
    TDeserializeRange,
    dealClassSearch,
} from '../utils/filtering/serializers/serializer.definitions';
import { createFilterActions } from './filter.actions';
import { filterUtils, getDateOptionByRange, getDateTimeOptionByRange, getTimeOptionsFromRange } from '../utils/filtering/filter.utils';
import { statsToCalculateWithoutMedians } from '../types/amr-pipeline/enums/StatsToCalculate';
import { TransactionClassResponse, TransactionResponse } from '../types/amr-pipeline/models/TransactionResponse';
import { history } from '../history';
import { amrCompaniesService } from '../services/amr-companies.service';
import { HavingDealType } from '../types/amr-pipeline/enums/HavingDealType';
import { RequestState } from '../constants/request-state';
import { KMatrixViewType } from '../types/state/AmrDealsState';
import { FilterOption } from '../types/filters/FilterOption';
import { TransactionType } from '../types/amr-pipeline/enums/TransactionType';
import { DealClassesArgument } from '../utils/filtering/serializers/DealClassesArgumentSerializer';
import { dateTimeUtils } from '../utils';
import { DateTimeRange } from '../types/filters/DateTimeRange';

type TDispatch = ThunkDispatch<AppState, void, UnknownAction>;

export const createAmrPipelineActions = (pipelineType: PipelineType) => {
    const filterActions = createFilterActions(pipelineType);
    const searchTransactionsActions = createSearchTransactionActions(pipelineType);

    function init() {
        return async (dispatch: TDispatch, getState: () => AppState) => {
            try {
                const { initialFilter } = amrPipelineFilterSelector(pipelineType)(getState());

                // load Filters for all tabs
                await dispatch(loadFilters());

                if (!initialFilter) {
                    dispatch(initFilter());

                    const hasImFiltersAccess = user.hasFeatures(SubscriptionFeature.IssuanceMonitorFiltersEmailAlerts);

                    if (hasImFiltersAccess) {
                        const {
                            imUserConfig: { filtersConfig },
                        } = getState();

                        let filterType =
                            pipelineType !== PipelineType.ArrangerPipeline
                                ? UserConfigType.imFilter
                                : UserConfigType.apFilter;

                        const selectedFilter = filtersConfig[filterType].value.find(filter => filter.default);
                        dispatch(filterActions.selectFilterFromConfig(selectedFilter, selectedFilter?.referenceName));
                    }

                    await dispatch(setFilterFromSearchString());
                }
                await dispatch(applyFilterAndSearch());
            } catch (e) {
                dispatch(errorActions.criticalError(e));
            }
        };
    }

    function filterExistingOptions(optionsNames: string[], optionFilter: FilterSelectGroup) {
        return optionsNames.filter(o => optionFilter.filter.find(f => f.value === o));
    }

    function setFilterFromSearchString() {
        return async (dispatch: TDispatch, getState: () => AppState) => {
            const { filter } = issuanceMonitorFilterSelector(pipelineType)(getState());
            const {
                imUserConfig: { filtersConfig },
            } = getState();

            const queryString = history.location.search;

            if (!queryString) {
                return;
            }

            // Available for all
            let arrangerReferenceNames: string[] = [];
            let collateralManagerReferenceNames: string[] = [];
            let transactionTypesValues: string[] = [];
            let currenciesValues: string[] = [];
            let statusValues: string[] = [];
            let collateralTypesValues: string[] = [];
            let ratingValues: string[] = [];

            // Only with IM subscription
            let filterReferenceName: string = '';
            let dealsReferenceNames: string[] = [];
            let dealsLegalNamesValues: string[] = [];
            let trusteesReferenceNames: string[] = [];
            let pricingDateFromValue: Date | null = null;
            let pricingDateToValue: Date | null = null;
            let closingDateFromValue: Date | null = null;
            let closingDateToValue: Date | null = null;
            let couponValue: string = '';
            let esgValue: boolean | undefined = undefined;
            let euComplianceValue: boolean | undefined = undefined;
            let staticDealValue: boolean | undefined = undefined;
            let isAmrValue: boolean | undefined = undefined;
            let outOfNCValue: boolean | undefined = undefined;
            let outOfRIValue: boolean | undefined = undefined;
            let hasNotesFlag: boolean | undefined;
            let ratingAgencyValues: string[] = [];
            let vintageFrom: Date | undefined | null;
            let vintageTo: Date | undefined | null;
            let mvocFromValue: number | undefined;
            let mvocToValue: number | undefined;
            let classNameValues: string[] = [];
            let debutCloFlag: boolean | undefined;
            let sofrFlag: boolean | undefined;
            let wasFromValue: number | undefined;
            let wasToValue: number | undefined;
            let warfFromValue: number | undefined;
            let warfToValue: number | undefined;
            let dsFromValue: number | undefined;
            let dsToValue: number | undefined;
            let rollerDeadlineFromValue: Date | null = null;
            let rollerDeadlineToValue: Date | null = null;
            let dealClassValues: DealClassesArgument[] = [];

            const arrangersSerializer = arrangers(
                referenceNames => (arrangerReferenceNames = filterExistingOptions(referenceNames, filter.arrangers)),
            );
            const collateralManagersSerializer = collateralManagers(
                collateralManagers =>
                    (collateralManagerReferenceNames = filterExistingOptions(collateralManagers, filter.managers)),
                filter.managers.filter.map(filter => filter.value as string),
            );
            const transactionTypesSerializer = transactionTypes(
                transactionTypes => (transactionTypesValues = transactionTypes),
            );
            const currenciesSerializer = multipleCurrencies(currencies => (currenciesValues = currencies));
            const statusSerializer = transactionStatuses(statuses => (statusValues = statuses));
            const collateralTypesSerializer = collateralTypes(types => (collateralTypesValues = types));
            const ratingsSerializer = ratings(ratings => (ratingValues = ratings));
            const filterSerializer = savedFilter(filter => (filterReferenceName = filter));
            const trusteesSerializer = trustees(names => (trusteesReferenceNames = filterExistingOptions(names, filter.trustees)));
            const dealsReferenceNamesSerializer = dealsSerializer(deals => (dealsReferenceNames = deals));
            const dealsLegalNamesSerializer = dealsLegalNames(deals => (dealsLegalNamesValues = deals));
            const pricingDateFromSerializer = pricingDateFrom(date => (pricingDateFromValue = date));
            const pricingDateToSerializer = pricingDateTo(date => (pricingDateToValue = date));
            const closingDateFromSerializer = closingDateFrom(date => (closingDateFromValue = date));
            const closingDateToSerializer = closingDateTo(date => (closingDateToValue = date));
            const couponSerializer = coupon(coupon => (couponValue = coupon));
            const esgSerializer = esg(esg => (esgValue = esg));
            const euComplianceSerializer = euCompliance(euCompliance => (euComplianceValue = euCompliance));
            const staticDealsSerializer = staticDeals(staticDeals => (staticDealValue = staticDeals));
            const isAmrSerializer = amr(amr => (isAmrValue = amr));
            const outOfNCSerializer = outOfNC(outOfNC => (outOfNCValue = outOfNC));
            const outOfRISerializer = outOfRI(outOfRI => (outOfRIValue = outOfRI));
            const hasNotesSerializer = hasNotes(flag => hasNotesFlag = flag);
            const ratingAgenciesSerializer = ratingAgencies(agencies => ratingAgencyValues = agencies);
            const vintageFromSerializer = vintageYearFrom(from => vintageFrom = from);
            const vintageToSerializer = vintageYearTo(to => vintageTo = to);
            const mvocFromSerializer = mvocFrom(value => mvocFromValue = Number(value));
            const mvocToSerializer = mvocTo(value => mvocToValue = Number(value));
            const classNamesSerializer = classNames(names => classNameValues = names);
            const debutCloSerializer = debutClo(flag => debutCloFlag = flag);
            const sofrSerializer = sofr(flag => sofrFlag = flag);
            const wasFromSerializer = wasFrom(value => wasFromValue = +value)
            const wasToSerializer = wasTo(value => wasToValue = +value)
            const warfFromSerializer = warfFrom(value => warfFromValue = +value)
            const warfToSerializer = warfTo(value => warfToValue = +value)
            const dsFromSerializer = dsFrom(value => dsFromValue = +value)
            const dsToSerializer = dsTo(value => dsToValue = +value)
            const [ncEndSerializers, getNcEndDeserializeResult] = mixedDateRange('nonCallEndFrom', 'nonCallEndTo');
            const [riEndSerializers, getRiEndDeserializeResult] = mixedDateRange('reinvestmentEndFrom', 'reinvestmentEndTo');
            const rollerDeadlineFromSerializer = rollerDeadlineFrom(date => rollerDeadlineFromValue = date);
            const rollerDeadlineToSerializer = rollerDeadlineTo(date => rollerDeadlineToValue = date);
            const dealClassSerializer = dealClassSearch(items => dealClassValues = items);

            const hasImFiltersAccess = user.hasFeatures(SubscriptionFeature.IssuanceMonitorFiltersEmailAlerts);

            const generalSerializers: QueryStringArgumentSerializer<any>[] = [
                collateralManagersSerializer,
                arrangersSerializer,
                transactionTypesSerializer,
                currenciesSerializer,
                ratingsSerializer,
                statusSerializer,
                collateralTypesSerializer,
            ];

            const imFilterSubscriptionSerializers: QueryStringArgumentSerializer<any>[] = [
                filterSerializer,
                dealsReferenceNamesSerializer,
                dealsLegalNamesSerializer,
                trusteesSerializer,
                ...ncEndSerializers,
                ...riEndSerializers,
                pricingDateFromSerializer,
                pricingDateToSerializer,
                couponSerializer,
                esgSerializer,
                euComplianceSerializer,
                staticDealsSerializer,
                isAmrSerializer,
                outOfNCSerializer,
                outOfRISerializer,
                hasNotesSerializer,
                ratingAgenciesSerializer,
                vintageFromSerializer,
                vintageToSerializer,
                mvocFromSerializer,
                mvocToSerializer,
                classNamesSerializer,
                debutCloSerializer,
                sofrSerializer,
                wasFromSerializer,
                wasToSerializer,
                warfFromSerializer,
                warfToSerializer,
                dsFromSerializer,
                dsToSerializer,
                closingDateFromSerializer,
                closingDateToSerializer,
                rollerDeadlineFromSerializer,
                rollerDeadlineToSerializer,
                dealClassSerializer
            ];

            const serializers = hasImFiltersAccess
                ? [...generalSerializers, ...imFilterSubscriptionSerializers]
                : generalSerializers;

            queryStringSerializer.deserialize(queryString, serializers);

            if (filterReferenceName) {
                let filterType =
                    pipelineType !== PipelineType.ArrangerPipeline ? UserConfigType.imFilter : UserConfigType.apFilter;

                const selectedFilter = filtersConfig[filterType].value.find(
                    filter => filter.referenceName === filterReferenceName,
                );

                return dispatch(filterActions.selectFilterFromConfig(selectedFilter, filterReferenceName));
            }

            const ncEndDeserializeResult = getNcEndDeserializeResult();
            const riEndDeserializeResult = getRiEndDeserializeResult();

            if (
                arrangerReferenceNames.length ||
                collateralManagerReferenceNames.length ||
                dealsReferenceNames.length ||
                dealsLegalNamesValues.length ||
                transactionTypesValues.length ||
                trusteesReferenceNames.length ||
                currenciesValues.length ||
                ncEndDeserializeResult?.dates ||
                ncEndDeserializeResult?.numeric ||
                riEndDeserializeResult?.dates ||
                riEndDeserializeResult?.numeric ||
                pricingDateFromValue ||
                pricingDateToValue ||
                closingDateFromValue ||
                closingDateToValue ||
                ratingValues.length ||
                couponValue ||
                !isNil(esgValue) ||
                !isNil(euComplianceValue) ||
                !isNil(staticDealValue) ||
                !isNil(isAmrValue) ||
                !isNil(outOfNCValue) ||
                !isNil(outOfRIValue) ||
                statusValues.length ||
                collateralTypesValues.length ||
                !isNil(hasNotesFlag) ||
                ratingAgencyValues.length ||
                !isNil(vintageFrom) ||
                !isNil(vintageTo) ||
                !isNil(mvocFromValue) ||
                !isNil(mvocToValue) ||
                classNameValues.length ||
                !isNil(debutCloFlag) ||
                !isNil(sofrFlag) ||
                !isNil(wasFromValue) ||
                !isNil(wasToValue) ||
                !isNil(warfFromValue) ||
                !isNil(warfToValue) ||
                !isNil(dsFromValue) ||
                !isNil(dsToValue) ||
                !isNil(rollerDeadlineFromValue) ||
                !isNil(rollerDeadlineToValue) ||
                dealClassValues.length
            ) {
                dispatch(resetFiltersAndSearch());
                dispatch(filterActions.resetFiltersVisibility());
            }

            if (dealClassValues.length) {
                dealClassValues.forEach(i =>
                    dispatch(
                        searchTransactionsActions.addSearchItem({
                            label: i.label,
                            referenceName: i.dealReferenceName,
                            classNames: i.classNames,
                        })
                    )
                );
            }

            if (!isNil(wasFromValue) || !isNil(wasToValue)) {
                dispatch(filterActions.makeFilterVisible('was'));
                dispatch(filterActions.changeRangeFilter({ from: wasFromValue, to: wasToValue }, 'was'));
            }

            if (!isNil(warfFromValue) || !isNil(warfToValue)) {
                dispatch(filterActions.makeFilterVisible('warf'));
                dispatch(filterActions.changeRangeFilter({ from: warfFromValue, to: warfToValue }, 'warf'));
            }

            if (!isNil(dsFromValue) || !isNil(dsToValue)) {
                dispatch(filterActions.makeFilterVisible('ds'));
                dispatch(filterActions.changeRangeFilter({ from: dsFromValue, to: dsToValue }, 'ds'));
            }

            if (!isNil(sofrFlag)) {
                dispatch(filterActions.makeFilterVisible('sofr'));
                dispatch(filterActions.filterRadioChange(sofrFlag, 'sofr'));
            }

            if (!isNil(debutCloFlag)) {
                dispatch(filterActions.makeFilterVisible('isDebut'));
                dispatch(filterActions.filterRadioChange(debutCloFlag, 'isDebut'));
            }

            if (classNameValues.length) {
                dispatch(filterActions.makeFilterVisible('classNames'));
                classNameValues.forEach(name =>
                    dispatch(filterActions.filterSelectChange(name, 'classNames')),
                );
            }

            if (!isNil(mvocFromValue) || !isNil(mvocToValue)) {
                dispatch(filterActions.makeFilterVisible('mvoc'));
                dispatch(filterActions.changeRangeFilter({ from: mvocFromValue, to: mvocToValue }, 'mvoc'))
            }

            if (rollerDeadlineFromValue || rollerDeadlineToValue) {
                const selectedOption = getDateTimeOptionByRange(
                    rollerDeadlineFromValue,
                    rollerDeadlineToValue
                );

                if (selectedOption) {
                    dispatch(filterActions.makeFilterVisible('rollerDeadline'));
                    dispatch(filterActions.filterDateTimeOptionSelected(selectedOption, 'rollerDeadline'));

                    const timeOptions = getTimeOptionsFromRange(rollerDeadlineFromValue, rollerDeadlineToValue);

                    const range: DateTimeRange = {
                        from: rollerDeadlineFromValue ? { date: rollerDeadlineFromValue, timeOption: timeOptions.from } : undefined,
                        to: rollerDeadlineToValue ? { date: rollerDeadlineToValue, timeOption: timeOptions.to } : undefined
                    }

                    dispatch(
                        filterActions.filterDateTimeSelectCustomRange(range, 'rollerDeadline'),
                    );
                }
            }

            if (!isNil(vintageFrom) || !isNil(vintageTo)) {
                dispatch(filterActions.makeFilterVisible('vintage'));
                const filterOption = getDateOptionByRange(dateRangeFilterOptions.ThisYear, vintageFrom, vintageTo);
                dispatch(filterActions.filterDateSelectOption(filterOption, 'vintage'));
                dispatch(
                    filterActions.filterDateSelectCustomRange({ from: vintageFrom, to: vintageTo }, 'vintage'),
                );
            }

            if (ratingAgencyValues.length) {
                dispatch(filterActions.makeFilterVisible('ratingAgencies'));
                ratingAgencyValues.forEach(agency =>
                    dispatch(filterActions.filterSelectChange(agency, 'ratingAgencies')),
                );
            }

            if (trusteesReferenceNames.length) {
                dispatch(filterActions.makeFilterVisible('trustees'));
            }

            if (riEndDeserializeResult?.dates || riEndDeserializeResult?.numeric) {
                dispatch(filterActions.filterDateQueryStringDeserialized('reinvestmentEnd', convertDaysToYears(riEndDeserializeResult)))
            }

            if (ncEndDeserializeResult?.dates || ncEndDeserializeResult?.numeric) {
                dispatch(filterActions.filterDateQueryStringDeserialized('nonCallEnd', convertDaysToYears(ncEndDeserializeResult)))
            }

            if (pricingDateFromValue || pricingDateToValue) {
                dispatch(filterActions.makeFilterVisible('pricingDate'));
                const filterOption = getDateOptionByRange(dateRangeFilterOptions.All, pricingDateFromValue, pricingDateToValue);
                dispatch(filterActions.filterDateSelectOption(filterOption, 'pricingDate'));
                dispatch(
                    filterActions.filterDateSelectCustomRange(
                        { from: pricingDateFromValue, to: pricingDateToValue },
                        'pricingDate',
                    ),
                );
            }

            if (closingDateFromValue || closingDateToValue) {
                dispatch(filterActions.makeFilterVisible('closingDate'));
                const filterOption = getDateOptionByRange(dateRangeFilterOptions.All, closingDateFromValue, closingDateToValue);
                dispatch(filterActions.filterDateSelectOption(filterOption, 'closingDate'));
                dispatch(
                    filterActions.filterDateSelectCustomRange({ from: closingDateFromValue, to: closingDateToValue }, 'closingDate'),
                );
            }

            if (couponValue) {
                dispatch(filterActions.makeFilterVisible('coupons'));
                dispatch(filterActions.filterRadioChange(couponValue, 'coupons'));
            }

            if (!isNil(hasNotesFlag)) {
                dispatch(filterActions.makeFilterVisible('hasNotes'));
                dispatch(filterActions.filterRadioChange(hasNotesFlag, 'hasNotes'));
            }

            if (!isNil(esgValue)) {
                dispatch(filterActions.makeFilterVisible('esg'));
                dispatch(filterActions.filterRadioChange(esgValue, 'esg'));
            }

            if (!isNil(euComplianceValue)) {
                dispatch(filterActions.makeFilterVisible('euCompliance'));
                dispatch(filterActions.filterRadioChange(euComplianceValue, 'euCompliance'));
            }

            if (!isNil(staticDealValue)) {
                dispatch(filterActions.makeFilterVisible('staticDeal'));
                dispatch(filterActions.filterRadioChange(staticDealValue, 'staticDeal'));
            }

            if (!isNil(isAmrValue)) {
                dispatch(filterActions.makeFilterVisible('amrDeal'));
                dispatch(filterActions.filterRadioChange(isAmrValue, 'amrDeal'));
            }

            if (!isNil(outOfNCValue)) {
                dispatch(filterActions.makeFilterVisible('outOfNC'));
                dispatch(filterActions.filterRadioChange(outOfNCValue, 'outOfNC'));
            }

            if (!isNil(outOfRIValue)) {
                dispatch(filterActions.makeFilterVisible('outOfRI'));
                dispatch(filterActions.filterRadioChange(outOfRIValue, 'outOfRI'));
            }

            arrangerReferenceNames.forEach(referenceName =>
                dispatch(filterActions.filterSelectChange(referenceName, 'arrangers')),
            );
            trusteesReferenceNames.forEach(referenceName =>
                dispatch(filterActions.filterSelectChange(referenceName, 'trustees')),
            );

            collateralManagerReferenceNames.forEach(referenceName =>
                dispatch(filterActions.filterSelectChange(referenceName, 'managers')),
            );

            dealsReferenceNames.forEach((referenceName, i) =>
                dispatch(
                    searchTransactionsActions.addSearchItem({
                        label: dealsLegalNamesValues[i] ?? referenceName,
                        referenceName,
                    }),
                ),
            );

            transactionTypesValues.forEach(transactionType =>
                dispatch(filterActions.filterSelectChange(transactionType, 'transactionTypes')),
            );

            currenciesValues.forEach(currency => dispatch(filterActions.filterSelectChange(currency, 'currency')));

            ratingValues.forEach(rating => dispatch(filterActions.filterSelectChange(rating, 'ratings')));

            statusValues.forEach(status => dispatch(filterActions.filterSelectChange(status, 'statuses')));

            collateralTypesValues.forEach(collateralType =>
                dispatch(filterActions.filterSelectChange(collateralType, 'collateralType'))
            );
        }
    }

    function handleVisibilityFilter(filterName: string) {
        return (dispatch: TDispatch, getState: () => AppState) => {
            dispatch(filterActions.filterVisibilityChange(filterName));
            const { filter, lastAppliedFilter } = amrPipelineFilterSelector(pipelineType)(getState());

            const filterChanged = filterUtils.isFilterChanged(pipelineType, filter, lastAppliedFilter);
            const currentFilter = filter[filterName];

            if (!currentFilter.visible) {
                dispatch(filterActions.updateFilterState());
                if (filterChanged) {
                    dispatch(apply());
                }
            }
        };
    }

    function loadFilters() {
        return async (dispatch: TDispatch, getState: () => AppState) => {
            const { initialFilter, redirectFilters } = issuanceMonitorFilterSelector(pipelineType)(getState());

            if (!initialFilter) {
                await Promise.all([
                    dispatch(
                        filterActions.storeFilterData.request({
                            filterName: 'managers',
                            filterType: pipelineType,
                        }),
                    ),
                    dispatch(
                        filterActions.storeFilterData.request({
                            filterName: 'arrangers',
                            filterType: pipelineType,
                        }),
                    ),
                    dispatch(
                        filterActions.storeFilterData.request({
                            filterName: 'classNames',
                            filterType: pipelineType,
                        }),
                    ),
                    dispatch(
                        filterActions.storeFilterData.request({
                            filterName: 'trustees',
                            filterType: pipelineType,
                        }),
                    ),
                ]);

                const [managers, arrangers, classNames, trustees]: [Company[], Company[], string[], Company[]] =
                    await Promise.all([
                        amrCompaniesService.getManagersList(HavingDealType.CmHavingAllPublishedDeals),
                        amrCompaniesService.getArrangersList(true),
                        amrPipelineService.getTransactionClassesNames(),
                        amrCompaniesService.getTrusteeList(),
                    ]);

                await Promise.all([
                    dispatch(
                        filterActions.storeFilterData.success({
                            filterName: 'managers',
                            filterType: pipelineType,
                            data: managers
                                .sort((a, b) => a.legalName.localeCompare(b.legalName))
                                .map(m => ({
                                    selected: false,
                                    disabled: false,
                                    visible: true,
                                    value: m.referenceName,
                                    text: m.legalName,
                                })),
                        }),
                    ),
                    dispatch(
                        filterActions.storeFilterData.success({
                            filterName: 'arrangers',
                            filterType: pipelineType,
                            data: arrangers
                                .sort((a, b) => a.legalName.localeCompare(b.legalName))
                                .map(a => ({
                                    selected: false,
                                    disabled: false,
                                    visible: true,
                                    value: a.referenceName,
                                    text: a.legalName + (a.code ? ' (' + a.code + ')' : ''),
                                })),
                        }),
                    ),
                    dispatch(
                        filterActions.storeFilterData.success({
                            filterName: 'classNames',
                            filterType: pipelineType,
                            data: classNames
                                .sort((a, b) => a.localeCompare(b))
                                .map(
                                    a =>
                                        redirectFilters?.classNames?.find(f => f.value === a) ?? {
                                            selected: false,
                                            disabled: false,
                                            visible: true,
                                            value: a,
                                            text: a,
                                        },
                                ),
                        }),
                    ),
                    dispatch(
                        filterActions.storeFilterData.success({
                            filterName: 'trustees',
                            filterType: pipelineType,
                            data: trustees
                                .sort((a, b) => a.legalName.localeCompare(b.legalName))
                                .map(t => ({
                                    selected: false,
                                    disabled: false,
                                    visible: true,
                                    value: t.referenceName,
                                    text: t.legalName,
                                })),
                        }),
                    ),
                ]);
            }
        };
    }

    function initFilter() {
        return (dispatch: TDispatch, getState: () => AppState) => {
            const { filter } = amrPipelineFilterSelector(pipelineType)(getState());
            const newFilter = { ...filter };

            (newFilter as DealsPipelineFilters).statuses.filter = getStatusFilters().map(s => ({
                ...s,
                selected: false,
                disabled: false,
                visible: true,
            }));

            dispatch(filterActions.init(newFilter));
            dispatch(filterActions.updateFilterState());
        };
    }

    function apply() {
        return (dispatch: TDispatch) => {
            dispatch(resetTransactions());
            dispatch(applyFilterAndSearch());
        };
    }

    function exportTransactionsClasses(exportedFrom: PipelineView) {
        return (dispatch: Dispatch, getState: () => AppState) => {
            const {
                deals: { classSortBy, classSortDirection, dealSortBy, dealSortDirection, kMatrixSortBy, kMatrixSortDirection, },
                searchTransactions: { searchTermItems },
            } = amrPipelineSelector(pipelineType)(getState());

            const { lastAppliedFilter } = amrPipelineFilterSelector(pipelineType)(getState());

            if (!lastAppliedFilter) return;

            dispatch(setExportLoading(true));

            const { deals, dealsClasses } = groupSearchByDealAndClassIndicators(searchTermItems);

            const exportCriteria = {
                ...getFilterCriteria(lastAppliedFilter, pipelineType),
                deals,
                dealsClasses,
                exportedFrom: convertPipelineViewToExportedFromKey(exportedFrom),
            };

            const classSortCriteria = mergeSortCriteria(
                classSortBy,
                classSortDirection,
                constants.defaultClassOrderCriteria,
            );
            const dealSortCriteria = mergeSortCriteria(
                dealSortBy,
                dealSortDirection,
                constants.defaultDealOrderCriteria,
            );

            const {
                orderByTransactionClass: kMatrixOrderByTransactionClass,
                orderByTransaction: kMatrixOrderByTransaction } = getKMatrixSortCriteria(
                    kMatrixSortBy,
                    kMatrixSortDirection,
                    constants.defaultDealOrderCriteria,
                );

            amrPipelineService
                .exportTransactionsClasses(
                    exportCriteria,
                    dealSortCriteria,
                    classSortCriteria,
                    kMatrixOrderByTransaction,
                    kMatrixOrderByTransactionClass)
                .then((file: { blob: Blob }) =>
                    saveAs(
                        file.blob,
                        `Issuance Monitor - ${moment().format(constants.issuanceMonitorExportDateFormat)}.xlsx`,
                    ),
                )
                .finally(() => dispatch(setExportLoading(false)));
        };
    }

    function reset() {
        return (dispatch: TDispatch) => {
            dispatch(filterActions.resetFilterState());
            dispatch({
                type: actionTypes.PIPELINE_RESET,
                pipelineType,
            });
        };
    }

    function searchTransactions(isSearching: boolean) {
        return {
            type: actionTypes.PIPELINE_SEARCH_TRANSACTIONS,
            pipelineType,
            isSearching,
        };
    }

    function searchClasses(isSearching: boolean) {
        return {
            type: actionTypes.PIPELINE_SEARCH_CLASSES,
            pipelineType,
            isSearching,
        };
    }

    function searchKMatrix(isSearching: boolean) {
        return {
            type: actionTypes.PIPELINE_SEARCH_K_MATRIX,
            pipelineType,
            isSearching,
        };
    }

    function dealSortingChanged(sortBy?: string, direction?: string) {
        return (dispatch: TDispatch) => {
            dispatch({
                type: actionTypes.PIPELINE_DEAL_SORTING_CHANGED,
                pipelineType,
                sortBy,
                direction,
            });
        };
    }

    function classSortingChanged(sortBy?: string, direction?: string) {
        return (dispatch: TDispatch) => {
            dispatch({
                type: actionTypes.PIPELINE_CLASS_SORTING_CHANGED,
                pipelineType,
                sortBy,
                direction,
            });
        };
    }

    function kMatrixSortingChanged(sortBy?: string, direction?: string) {
        return (dispatch: TDispatch) => {
            dispatch({
                type: actionTypes.PIPELINE_K_MATRIX_SORTING_CHANGED,
                pipelineType,
                sortBy,
                direction,
            });
        };
    }

    function summaryRequesting(requestState: RequestState) {
        return {
            type: actionTypes.PIPELINE_SUMMARY_REQUESTING_STATE,
            pipelineType,
            requestState,
        };
    }

    function loadSummary() {
        return async (dispatch: TDispatch, getState: () => AppState) => {
            const {
                filter: { lastAppliedFilter },
                searchTransactions: { searchTermItems },
            } = amrPipelineSelector(pipelineType)(getState());

            if (!lastAppliedFilter) return;

            const { shouldShowTransactionsSummary, shouldShowClassesSummary } = summarySelector(pipelineType)(
                getState(),
            );

            const filterCriteria = {
                ...getFilterCriteria(lastAppliedFilter, pipelineType),
                deals: searchTermItems.map(i => i.referenceName),
                count: constants.defaultTransactionsSearchCriteria.count,
            };

            dispatch(summaryRequesting(RequestState.request));

            try {
                const transactionsSummaryPromise = amrPipelineService.getTransactionsSummary(
                    filterCriteria,
                    shouldShowTransactionsSummary ? undefined : statsToCalculateWithoutMedians,
                );
                const classesSummaryPromise = shouldShowClassesSummary
                    ? amrPipelineService.getTransactionClassesSummary(filterCriteria)
                    : null;

                const [transactionsSummary, classesSummary] = await Promise.all([
                    transactionsSummaryPromise,
                    classesSummaryPromise,
                ]);

                dispatch({
                    type: actionTypes.PIPELINE_STORE_SUMMARY,
                    pipelineType,
                    transactionsSummary,
                    classesSummary,
                    summaryRequestState: RequestState.success,
                });
            } catch (error) {
                dispatch(errorActions.error(error, 'Error loading summary'));
                dispatch({
                    type: actionTypes.PIPELINE_STORE_SUMMARY,
                    pipelineType,
                    transactionsSummary: undefined,
                    classesSummary: undefined,
                    summaryRequestState: RequestState.failure,
                });
            }
        };
    }

    function applyFilterAndSearch(view?: PipelineView, autoExport = false) {
        return async (dispatch: TDispatch) => {
            const loadFunction =
                view === PipelineView.Class ? loadClasses : view === PipelineView.Deal ? loadTransactions : loadKMatrix;

            dispatch(filterActions.applyFilter());

            try {
                if (!view || autoExport) {
                    await Promise.all([
                        dispatch(loadClasses()),
                        dispatch(loadTransactions()),
                        dispatch(loadKMatrix()),
                        user.hasFeatures(SubscriptionFeature.IssuanceMonitorFullAccess)
                            ? dispatch(loadSummary())
                            : null,
                    ]);

                    dispatch(redirectWithFilterArgs());
                } else {
                    await Promise.all([
                        dispatch(loadFunction()),
                        user.hasFeatures(SubscriptionFeature.IssuanceMonitorFullAccess)
                            ? dispatch(loadSummary())
                            : null,
                    ]);
                }

                if (autoExport && view) {
                    dispatch(exportTransactionsClasses(view));
                }
            } catch (e) {
                dispatch(filterActions.resetFilter());
                dispatch(errorActions.criticalError(e));
            }
        };
    }

    function redirectWithFilterArgs() {
        return (dispatch: TDispatch, getState: () => AppState) => {
            const {
                searchTransactions: { searchTermItems },
            } = amrPipelineSelector(pipelineType)(getState());
            const { lastAppliedFilter } = amrPipelineFilterSelector(pipelineType)(getState());

            if (!lastAppliedFilter) return;

            const { deals, dealsClasses } = groupSearchByDealAndClassIndicators(searchTermItems);

            const filter = {
                ...getFilterCriteria(lastAppliedFilter, pipelineType),
                deals,
                dealsClasses,
            };

            const serializers: QueryStringArgumentSerializer<any>[] = [
                ratings(),
                collateralManagers(),
                arrangers(),
                pricingDateFrom(), pricingDateTo(),
                closingDateFrom(), closingDateTo(),
                nonCallEndFrom(), nonCallEndTo(),
                reinvestmentEndFrom(), reinvestmentEndTo(),
                rollerDeadlineFrom(), rollerDeadlineTo(),
                wasFrom(), wasTo(),
                warfFrom(), warfTo(),
                dsFrom(), dsTo(),
                collateralTypes(),
                amr(),
                euCompliance(),
                multipleCurrencies(),
                esg(),
                sofr(),
                debutClo(),
                staticDeals(),
                classNames(),
                mvocFrom(), mvocTo(),
                trustees(),
                vintageYearFrom(), vintageYearTo(),
                coupon(),
                outOfNC(),
                outOfRI(),
                ratingAgencies(),
                hasNotes(),
                dealsSerializer(),
                dealsLegalNames(),
                dealClassSearch()
            ];

            if (lastAppliedFilter?.statuses.filter.some(x => x.selected)) {
                serializers.push(transactionStatuses());
            }
            if (lastAppliedFilter?.transactionTypes.filter.some(x => x.selected)) {
                serializers.push(transactionTypes())
            }

            const adaptedFilter = {
                ...filter,
                transactionTypes: filter.types,
                amr: filter.amrDeal,
                staticDeals: filter.staticDeal,
                coupon: filter.coupons,
                transactionStatuses: filter.statuses,
                currency: filter.currencyCodes,
                mvocFrom: filter.mvoc?.from,
                mvocTo: filter.mvoc?.to,
                vintageFrom: filter.vintage.from,
                vintageTo: filter.vintage.to,
                wasFrom: filter.was?.from,
                wasTo: filter.was?.to,
                warfFrom: filter.warf?.from,
                warfTo: filter.warf?.to,
                dsFrom: filter.ds?.from,
                dsTo: filter.ds?.to,
                pricingDateFrom: filter.pricingDate?.from,
                pricingDateTo: filter.pricingDate?.to,
                closingDateFrom: filter.closingDate?.from,
                closingDateTo: filter.closingDate?.to,
                nonCallEndFrom: filter.nonCallPeriodEndDays?.from ?? filter.nonCallPeriodEnd?.from,
                nonCallEndTo: filter.nonCallPeriodEndDays?.to ?? filter.nonCallPeriodEnd?.to,
                reinvestmentEndFrom: filter?.reinvestmentPeriodEndDays?.from ?? filter.reinvestmentPeriodEnd?.from,
                reinvestmentEndTo: filter.reinvestmentPeriodEndDays?.to ?? filter.reinvestmentPeriodEnd?.to,
                rollerDeadlineFrom: filter.rollerDeadline?.from ? dateTimeUtils.resetTimeZone(filter.rollerDeadline?.from) : undefined,
                rollerDeadlineTo: filter.rollerDeadline?.to ? dateTimeUtils.resetTimeZone(filter.rollerDeadline?.to) : undefined,
                dealsClasses: dealsClasses.map(d => ({ // Add labels
                    ...d,
                    label: searchTermItems
                        .find(i => i.referenceName === d.dealReferenceName)
                        ?.label ?? ''
                }))
            }

            const queryString = queryStringSerializer.serialize(adaptedFilter, serializers);

            history.replace(history.location.pathname + '?' + queryString);
        }
    }

    function checkRefiResetFilterOnly(options: FilterOption<TransactionType>[]) {
        return (
            options.length > 0 &&
            !options.some(s =>
                s.value !== TransactionType.refi &&
                s.value !== TransactionType.reset)
        );
    }

    function checkNewIssueFilterOnly(options: FilterOption<TransactionType>[]) {
        return (
            options.length > 0 &&
            !options.some(s => s.value !== TransactionType.newIssue)
        );
    }


    function loadKMatrix() {
        return async (dispatch: TDispatch, getState: () => AppState) => {
            const {
                deals: { kMatrixSortBy, kMatrixSortDirection, kMatrixOffset, totalKMatrixClassItemsCount },
                searchTransactions: { searchTermItems },
            } = amrPipelineSelector(pipelineType)(getState());
            const { lastAppliedFilter } = amrPipelineFilterSelector(pipelineType)(getState());

            if (!lastAppliedFilter) return;

            const filterCriteria = {
                ...getFilterCriteria(lastAppliedFilter, pipelineType),
                deals: searchTermItems.map(i => i.referenceName),
                count: constants.defaultTransactionsSearchCriteria.count,
            };

            const dealSortCriteria = getKMatrixSortCriteria(
                kMatrixSortBy,
                kMatrixSortDirection,
                constants.defaultDealOrderCriteria,
            );

            dispatch(searchKMatrix(true));

            try {
                const kMatrixList: any = await amrPipelineService.getKMatrixList(
                    { ...filterCriteria, offset: kMatrixOffset, totalClassItemsCount: totalKMatrixClassItemsCount },
                    dealSortCriteria,
                );

                dispatch({
                    type: actionTypes.PIPELINE_STORE_K_MATRIX,
                    pipelineType,
                    kMatrixList,
                });

                dispatch(changeKMatrixViewType(lastAppliedFilter));
            } catch (e) {
                dispatch(errorActions.criticalError(e));
            } finally {
                dispatch(searchKMatrix(false));
            }
        };
    }

    function changeKMatrixViewType(filter: DealsPipelineFilters) {
        return (dispatch: TDispatch, getState: () => AppState) => {

            const { kMatrixViewType, kMatrixViewTypeAutoChange } = getState().issuanceMonitor.amrPipeline.deals;

            // Do not override the view type if user selected it manyally
            // kMatrixViewTypeAutoChange === undefined - initial state
            // kMatrixViewTypeAutoChange === true - changed by the system
            // kMatrixViewTypeAutoChange === false - changed by the user
            if (kMatrixViewTypeAutoChange === false) return;

            const transactionTypeSelectedOptions = filter.transactionTypes.filter.filter(s => s.selected);
            const isRefiResetOnly = checkRefiResetFilterOnly(transactionTypeSelectedOptions);
            const isNewIssueOnly = checkNewIssueFilterOnly(transactionTypeSelectedOptions);


            if (isRefiResetOnly) {
                if (kMatrixViewType !== KMatrixViewType.MVOC) {
                    dispatch(kMatrixViewTypeChanged(KMatrixViewType.MVOC, true));
                }
            } else if (isNewIssueOnly) {
                if (kMatrixViewType !== KMatrixViewType.CE) {
                    dispatch(kMatrixViewTypeChanged(KMatrixViewType.CE, true));
                }
            } else if (kMatrixViewType !== KMatrixViewType.Default) {
                dispatch(kMatrixViewTypeChanged(KMatrixViewType.Default, true));
            }
        };
    }

    function loadTransactions() {
        return async (dispatch: TDispatch, getState: () => AppState) => {
            const {
                deals: { dealSortBy, dealSortDirection, transactionsOffset },
                searchTransactions: { searchTermItems },
            } = amrPipelineSelector(pipelineType)(getState());
            const { lastAppliedFilter } = amrPipelineFilterSelector(pipelineType)(getState());

            if (!lastAppliedFilter) return;

            const { deals, dealsClasses } = groupSearchByDealAndClassIndicators(searchTermItems);

            const filterCriteria = {
                ...getFilterCriteria(lastAppliedFilter, pipelineType),
                deals,
                dealsClasses,
                count: 40,
            };

            const dealSortCriteria = mergeSortCriteria(
                dealSortBy,
                dealSortDirection,
                constants.defaultDealOrderCriteria,
            );

            dispatch(searchTransactions(true));

            try {
                const transactions: TransactionResponse = await amrPipelineService.getTransactionsList(
                    { ...filterCriteria, offset: transactionsOffset },
                    dealSortCriteria,
                );

                dispatch(searchTransactions(false));
                dispatch({
                    type: actionTypes.PIPELINE_STORE_TRANSACTIONS,
                    pipelineType,
                    transactions
                });

                if (pipelineType === PipelineType.IOIs && !transactions.items.length) {
                    const oneIOIs: TransactionResponse = await amrPipelineService.getTransactionsList({
                        hasIOIs: true,
                        includeIois: true,
                        count: 1,
                    });

                    const anyIOIsExists = !!oneIOIs.items.length;
                    dispatch({
                        type: actionTypes.PIPELINE_SET_ANY_IOIS_EXISTS,
                        pipelineType,
                        anyIOIsExists,
                    });
                }
            } catch (e) {
                dispatch(searchTransactions(false));
                dispatch(errorActions.criticalError(e));
            }
        };
    }

    function loadClasses() {
        return async (dispatch: TDispatch, getState: () => AppState) => {
            const {
                deals: { classSortBy, classSortDirection, classesOffset },
                searchTransactions: { searchTermItems },
            } = amrPipelineSelector(pipelineType)(getState());
            const { lastAppliedFilter } = amrPipelineFilterSelector(pipelineType)(getState());

            if (!lastAppliedFilter) return;

            const { deals, dealsClasses } = groupSearchByDealAndClassIndicators(searchTermItems);

            const filterCriteria = {
                ...getFilterCriteria(lastAppliedFilter, pipelineType),
                deals,
                dealsClasses,
                count: 40,
            };

            const classSortCriteria = mergeSortCriteria(
                classSortBy,
                classSortDirection,
                constants.defaultClassOrderCriteria,
            );

            dispatch(searchClasses(true));

            try {
                const classes: TransactionClassResponse = await amrPipelineService.getTransactionClasses(
                    { ...filterCriteria, offset: classesOffset },
                    classSortCriteria,
                );

                dispatch(searchClasses(false));
                dispatch({
                    type: actionTypes.PIPELINE_STORE_CLASSES,
                    pipelineType,
                    classes,
                });
            } catch (e) {
                dispatch(searchClasses(false));
                dispatch(errorActions.criticalError(e));
            }
        };
    }

    function resetFiltersAndSearch() {
        return (dispatch: TDispatch) => {
            dispatch(filterActions.resetFiltersAndUnselectSavedFilter());
            dispatch(searchTransactionsActions.reset());
        };
    }

    function changeDealSelectFromTable(dealLegalName: string, dealReferenceName: string) {
        return (dispatch: TDispatch) => {
            dispatch(resetFiltersAndSearch());
            dispatch(resetTransactions());
            dispatch(
                searchTransactionsActions.addSearchItem({
                    label: dealLegalName,
                    referenceName: dealReferenceName,
                }),
            );
        };
    }

    function applyDealSelectFromTable(dealLegalName: string, dealReferenceName: string) {
        return (dispatch: TDispatch, getState: () => AppState) => {
            const {
                searchTransactions: { searchTermItems },
            } = amrPipelineSelector(pipelineType)(getState());
            const isDealSelected =
                searchTermItems.some(i => i.referenceName === dealReferenceName) && searchTermItems.length === 1;

            dispatch(resetFiltersAndSearch());

            isDealSelected
                ? dispatch(searchTransactionsActions.removeSearchItem(0))
                : dispatch(
                    searchTransactionsActions.addSearchItem({
                        label: dealLegalName,
                        referenceName: dealReferenceName,
                    }),
                );

            dispatch(apply());
        };
    }

    function filterReset() {
        return (dispatch: TDispatch) => {
            dispatch(filterActions.resetFiltersAndUnselectSavedFilter());
            dispatch(apply());
        };
    }

    function resetTransactions() {
        return {
            type: actionTypes.PIPELINE_RESET_TRANSACTIONS,
            pipelineType,
        };
    }

    function setExpanded(expanded: boolean) {
        return {
            type: actionTypes.PIPELINE_SET_EXPANDED,
            pipelineType,
            expanded,
        };
    }

    function setExportLoading(isLoading: boolean) {
        return {
            type: actionTypes.PIPELINE_EXPORT_LOAD,
            pipelineType,
            isLoading,
        };
    }

    function setSyndicateContacts(
        dealLegalName: string,
        dealReferenceName: string,
        transactionReferenceName: string,
        arrangerName: string,
    ) {
        return async (dispatch: TDispatch) => {
            try {
                const contacts = await amrPipelineService.getSyndicateContacts(
                    dealReferenceName,
                    transactionReferenceName,
                );

                dispatch({
                    type: actionTypes.PIPELINE_SET_SYNDICATE_CONTACTS,
                    pipelineType,
                    transactionName: dealLegalName,
                    contacts,
                    arrangerName,
                });
            } catch (e) {
                dispatch(errorActions.criticalError(e));
            }
        };
    }

    function resetSyndicateContacts() {
        return {
            type: actionTypes.PIPELINE_SET_SYNDICATE_CONTACTS,
            pipelineType,
            syndicateContacts: null,
        };
    }

    function transactionNoteAdded(dealReferenceName: string, transactionReferenceName: string) {
        return {
            type: actionTypes.PIPELINE_TRANSACTION_NOTE_ADDED,
            pipelineType,
            dealReferenceName,
            transactionReferenceName
        };
    }

    function transactionNoteDeleted(dealReferenceName: string, transactionReferenceName: string, count: number = 1) {
        return {
            type: actionTypes.PIPELINE_TRANSACTION_NOTE_DELETED,
            pipelineType,
            count,
            dealReferenceName,
            transactionReferenceName
        };
    }

    function kMatrixViewTypeChanged(viewType: KMatrixViewType, autoChange: boolean) {
        return {
            type: actionTypes.PIPELINE_K_MATRIX_VIEW_TYPE_CHANGED,
            pipelineType,
            viewType,
            autoChange
        };
    }

    return {
        init,
        reset,
        handleVisibilityFilter,
        filterReset,
        resetFiltersAndSearch,
        apply,
        searchTransactions,
        resetTransactions,
        dealSortingChanged,
        classSortingChanged,
        kMatrixSortingChanged,
        setExpanded,
        applyFilterAndSearch,
        exportTransactionsClasses,
        changeDealSelectFromTable,
        applyDealSelectFromTable,
        loadTransactions,
        loadClasses,
        loadKMatrix,
        setSyndicateContacts,
        resetSyndicateContacts,
        loadFilters,
        initFilter,
        transactionNoteAdded,
        transactionNoteDeleted,
        kMatrixViewTypeChanged
    };
};

function convertDaysToYears(r: TDeserializeRange): TDeserializeRange {
    const { numeric } = r;

    if (!numeric) return r;

    return {
        ...r,
        numeric: {
            from: numeric.from ? round(numeric.from / 365, 2) : numeric.from,
            to: numeric.to ? round(numeric.to / 365, 2) : numeric.to
        }
    }
}
